import React, { useState, useEffect, useMemo } from 'react';
import './BookCover.css';

// Curated palette of dignified academic book-cloth colors
const ACADEMIC_PALETTES = [
  {
    bg: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)', // Oxford Navy
    accent: '#93c5fd',
    foil: '#fde047',
    icon: 'bi-book-half'
  },
  {
    bg: 'linear-gradient(135deg, #7f1d1d 0%, #450a0a 100%)', // Imperial Burgundy
    accent: '#fca5a5',
    foil: '#fef08a',
    icon: 'bi-journal-bookmark-fill'
  },
  {
    bg: 'linear-gradient(135deg, #064e3b 0%, #022c22 100%)', // Forest Emerald
    accent: '#6ee7b7',
    foil: '#fde047',
    icon: 'bi-bookmark-star-fill'
  },
  {
    bg: 'linear-gradient(135deg, #063547 0%, #021a24 100%)', // Vidyajyoti Deep Teal
    accent: '#7ee8fa',
    foil: '#fbbf24',
    icon: 'bi-mortarboard-fill'
  },
  {
    bg: 'linear-gradient(135deg, #581c87 0%, #2e1065 100%)', // Royal Plum
    accent: '#d8b4fe',
    foil: '#fde047',
    icon: 'bi-journal-richtext'
  },
  {
    bg: 'linear-gradient(135deg, #78350f 0%, #451a03 100%)', // Vintage Leather Saddle
    accent: '#fcd34d',
    foil: '#fef08a',
    icon: 'bi-book'
  },
  {
    bg: 'linear-gradient(135deg, #334155 0%, #0f172a 100%)', // Slate Charcoal
    accent: '#cbd5e1',
    foil: '#e2e8f0',
    icon: 'bi-journals'
  },
  {
    bg: 'linear-gradient(135deg, #831843 0%, #500724 100%)', // Deep Crimson
    accent: '#f472b6',
    foil: '#fde047',
    icon: 'bi-award-fill'
  }
];

export default function BookCover({
  title = '',
  author = '',
  edition = '',
  category = '',
  callNumber = '',
  accessionNo = '',
  imageUrl = '',
  size = 'md', // 'sm', 'md', 'lg', 'xl'
  style = {},
  className = '',
  onClick = null
}) {
  const [imageError, setImageError] = useState(false);

  // Reset error state if image URL changes
  useEffect(() => {
    setImageError(false);
  }, [imageUrl]);

  // Compute a deterministic palette based on title or accession number
  const palette = useMemo(() => {
    const seed = (title || '') + (accessionNo || '') + (author || '');
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      hash = (hash << 5) - hash + seed.charCodeAt(i);
      hash |= 0;
    }
    const index = Math.abs(hash) % ACADEMIC_PALETTES.length;
    return ACADEMIC_PALETTES[index];
  }, [title, accessionNo, author]);

  // Format version or edition display
  const displayVersion = useMemo(() => {
    if (!edition || edition === 'NA' || edition === '1' || edition === '1st') return '';
    return String(edition).toLowerCase().includes('ed') ? edition : `${edition} Ed.`;
  }, [edition]);

  const hasValidImage = imageUrl && !imageError && !imageUrl.includes('cat1.png');

  if (hasValidImage) {
    return (
      <div
        className={`vj-book-jacket vj-book-jacket-${size} ${className}`}
        style={style}
        onClick={onClick}
      >
        <img
          src={imageUrl}
          alt={title || 'Book Cover'}
          onError={() => setImageError(true)}
          className="vj-book-img-cover"
          loading="lazy"
        />
      </div>
    );
  }

  // Realistic Hardcover Jacket Rendering
  return (
    <div
      className={`vj-book-jacket vj-book-jacket-${size} ${className}`}
      style={{
        background: palette.bg,
        ...style
      }}
      onClick={onClick}
      title={`${title || 'Book'}${author ? ` by ${author}` : ''}`}
    >
      {/* 3D Spine Crease */}
      <div className="vj-jacket-spine"></div>

      {/* Foil Inner Frame */}
      <div className="vj-jacket-inner-frame"></div>

      {/* Book-Cloth Grain Texture */}
      <div className="vj-jacket-texture"></div>

      {/* Content Layout */}
      <div className="vj-jacket-content">
        {/* Top Crest / Category Header */}
        <div className="vj-jacket-header">
          <span className="vj-jacket-crest" style={{ color: (author || '').trim().toLowerCase() === 'bound journal' ? '#7ee8fa' : palette.accent }}>
            <i className={`bi ${(author || '').trim().toLowerCase() === 'bound journal' ? 'bi-journal-bookmark-fill' : palette.icon} me-1`}></i>
            {(author || '').trim().toLowerCase() === 'bound journal' ? 'BOUND JOURNAL' : (category && category !== 'NA' ? category : 'LIBRARY')}
          </span>
          <span className="vj-jacket-ribbon" style={{ color: palette.foil }}>
            <i className="bi bi-bookmark-fill"></i>
          </span>
        </div>

        {/* Center: Book Title & Subtitle */}
        <div>
          <div className="vj-jacket-title">{title || 'Library Book'}</div>
          {displayVersion && (
            <div className="vj-jacket-version" style={{ color: palette.foil }}>
              {displayVersion}
            </div>
          )}
        </div>

        {/* Bottom Footer: Author & Call No */}
        <div className="vj-jacket-footer">
          <div className="vj-jacket-author" style={{ color: palette.foil }}>
            {author || 'Vidyajyoti Library'}
          </div>
          {callNumber && callNumber !== 'NA' ? (
            <div className="vj-jacket-callno">{callNumber}</div>
          ) : accessionNo ? (
            <div className="vj-jacket-callno">#{accessionNo}</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
